import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Globe, Menu, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const Header = () => {
  const { t, language, changeLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80 border-b border-green-900/20">
      {/* Announcement Bar */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-2 px-4 text-center text-sm font-medium">
        {t.hero.announcement}
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform duration-200">
              <span className="text-black font-bold text-lg">G</span>
            </div>
            <span className="text-white font-bold text-xl">GERVIS</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center space-x-1 px-4 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">
                <span>{t.nav.industries}</span>
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-black border-green-900/20">
                <DropdownMenuItem className="text-gray-300 hover:text-white hover:bg-green-600/10">
                  <Link to="/industries/health">{t.footer.health}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="text-gray-300 hover:text-white hover:bg-green-600/10">
                  <Link to="/industries/realestate">{t.footer.realestate}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="text-gray-300 hover:text-white hover:bg-green-600/10">
                  <Link to="/industries/insurance">{t.footer.insurance}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/ai-agents" className="px-4 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">
              {t.nav.aiAgents}
            </Link>

            <Link to="/about" className="px-4 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">
              {t.nav.about}
            </Link>

            <Link to="/partners" className="px-4 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">
              {t.nav.partners}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center space-x-1 px-4 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">
                <span>{t.nav.resources}</span>
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-black border-green-900/20">
                <DropdownMenuItem className="text-gray-300 hover:text-white hover:bg-green-600/10">
                  <Link to="/blog">{t.footer.blog}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="text-gray-300 hover:text-white hover:bg-green-600/10">
                  <Link to="/cases">{t.footer.cases}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="text-gray-300 hover:text-white hover:bg-green-600/10">
                  <Link to="/contact">{t.contact.title}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right Section */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center space-x-1 px-3 py-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/5">
                <Globe className="w-4 h-4" />
                <span className="uppercase text-sm">{language}</span>
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-black border-green-900/20">
                <DropdownMenuItem 
                  onClick={() => changeLanguage('es')} 
                  className="text-gray-300 hover:text-white hover:bg-green-600/10"
                >
                  Español
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('en')} 
                  className="text-gray-300 hover:text-white hover:bg-green-600/10"
                >
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/login" className="text-gray-300 hover:text-white transition-colors">
              {t.nav.login}
            </Link>

            <Link to="/demo">
              <Button className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-full px-6 py-2 flex items-center space-x-2 shadow-lg shadow-green-600/30 transition-all duration-200 hover:scale-105">
                <span>{t.nav.tryGervis}</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-green-900/20">
          <div className="container mx-auto px-4 py-4 space-y-3">
            <Link to="/ai-agents" className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
              {t.nav.aiAgents}
            </Link>
            <Link to="/about" className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
              {t.nav.about}
            </Link>
            <Link to="/partners" className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
              {t.nav.partners}
            </Link>
            <Link to="/contact" className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
              {t.contact.title}
            </Link>
            <div className="pt-4 border-t border-green-900/20">
              <Link to="/demo">
                <Button className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-full">
                  {t.nav.tryGervis}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;